import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CheckCircle2, Home, Calendar, Mail, Clock, MapPin, Users, DollarSign, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

interface CustomAdventureRequestDetails {
  requestNumber: string;
  adventureName: string;
  businessName: string;
  dateRange: {
    startDate: string;
    endDate: string;
    flexibility: string;
  };
  participants: {
    adults: number;
    children: number;
    students: number;
  };
  estimatedPrice: {
    totalEstimate: number;
    currency: string;
  };
  priceBreakdown?: Array<{ label: string; amount: number; description?: string }>;
  customerDetails: {
    name: string;
    email: string;
    phone: string;
    specialRequests?: string;
  };
  selectedOptions: Record<string, any>;
  status: 'pending' | 'accepted' | 'rejected' | 'modified';
  createdAt: string;
}

export default function CustomAdventureRequestConfirmation() {
  const navigate = useNavigate();
  const { requestNumber } = useParams<{ requestNumber: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures.confirmation' });
  const { t: tCommon } = useTranslation('common');
  
  // Get data from URL params (passed from form submission)
  const [requestDetails, setRequestDetails] = useState<CustomAdventureRequestDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // For now, we'll get the data from URL search params
    // In the future, this could be fetched from an API
    const data = searchParams.get('data');
    
    if (data) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(data));
        setRequestDetails(parsedData);
      } catch (error) {
        console.error('Error parsing confirmation data:', error);
      }
    }
    
    setLoading(false);
  }, [searchParams]);

  const getFlexibilityText = (flexibility: string) => {
    switch (flexibility) {
      case 'exact':
        return t('flexibility.exact');
      case 'flexible_1_week':
        return t('flexibility.oneWeek');
      case 'flexible_2_weeks':
        return t('flexibility.twoWeeks');
      case 'flexible_1_month':
        return t('flexibility.oneMonth');
      default:
        return flexibility;
    }
  };

  const getTotalParticipants = () => {
    if (!requestDetails) return 0;
    return requestDetails.participants.adults + requestDetails.participants.children + requestDetails.participants.students;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#77A146]" />
      </div>
    );
  }

  if (!requestDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('notFound.title')}</h1>
          <p className="text-gray-600 mb-4">{t('notFound.description')}</p>
          <Button onClick={() => navigate('/')} className="bg-[#77A146] hover:bg-[#6a9139]">
            <Home className="h-4 w-4 mr-2" />
            {t('notFound.backHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F0] p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="flex items-center gap-2 bg-white hover:bg-gray-50"
          >
            <Home className="h-4 w-4" />
            {tCommon('actions.backHome')}
          </Button>
        </div>

        {/* Main Confirmation Card */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-6">
          {/* Success Header */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <CheckCircle2 className="h-20 w-20 text-[#77A146]" />
              <div className="absolute -top-1 -right-1 h-6 w-6 bg-[#77A146] rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-center mb-2 text-gray-900">
            {t('title')}
          </h1>
          <p className="text-center text-gray-600 mb-2">
            {t('subtitle')}
          </p>
          <p className="text-center text-lg font-semibold text-[#77A146] mb-8">
            {t('requestNumber')}: {requestDetails.requestNumber}
          </p>

          {/* Status Badge */}
          <div className="flex justify-center mb-8">
            <Badge variant="secondary" className="bg-amber-100 text-amber-800 px-4 py-2 text-sm">
              <Clock className="h-4 w-4 mr-2" />
              {t('status.pending')}
            </Badge>
          </div>
        </div>

        {/* Next Steps Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start">
            <Mail className="h-6 w-6 text-blue-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                {t('nextSteps.title')}
              </h3>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start">
                  <span className="inline-block w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">1</span>
                  {t('nextSteps.step1')}
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">2</span>
                  {t('nextSteps.step2')}
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">3</span>
                  {t('nextSteps.step3')}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Request Details Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Adventure Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
              <MapPin className="h-5 w-5 mr-2 text-[#77A146]" />
              {t('sections.adventure')}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">{t('fields.adventureName')}</p>
                <p className="font-medium text-gray-900">{requestDetails.adventureName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.business')}</p>
                <p className="font-medium text-gray-900">{requestDetails.businessName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.requestDate')}</p>
                <p className="font-medium text-gray-900">
                  {format(new Date(requestDetails.createdAt), 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
          </div>

          {/* Date & Participants */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-[#77A146]" />
              {t('sections.dateParticipants')}
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">{t('fields.dateRange')}</p>
                <p className="font-medium text-gray-900">
                  {format(new Date(requestDetails.dateRange.startDate), 'MMM d, yyyy')} - {' '}
                  {format(new Date(requestDetails.dateRange.endDate), 'MMM d, yyyy')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.flexibility')}</p>
                <p className="font-medium text-gray-900">
                  {getFlexibilityText(requestDetails.dateRange.flexibility)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.participants')}</p>
                <div className="flex items-center text-gray-900">
                  <Users className="h-4 w-4 mr-2" />
                  <span className="font-medium">{getTotalParticipants()} {t('fields.total')}</span>
                </div>
                <div className="mt-1 text-sm text-gray-600 space-y-1">
                  {requestDetails.participants.adults > 0 && (
                    <div>• {requestDetails.participants.adults} {t('fields.adults')}</div>
                  )}
                  {requestDetails.participants.children > 0 && (
                    <div>• {requestDetails.participants.children} {t('fields.children')}</div>
                  )}
                  {requestDetails.participants.students > 0 && (
                    <div>• {requestDetails.participants.students} {t('fields.students')}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900">
            {t('sections.customerDetails')}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('fields.customerName')}</p>
              <p className="font-medium text-gray-900">{requestDetails.customerDetails.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('fields.email')}</p>
              <p className="font-medium text-gray-900">{requestDetails.customerDetails.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('fields.phone')}</p>
              <p className="font-medium text-gray-900">{requestDetails.customerDetails.phone}</p>
            </div>
            {requestDetails.customerDetails.specialRequests && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">{t('fields.specialRequests')}</p>
                <p className="font-medium text-gray-900">{requestDetails.customerDetails.specialRequests}</p>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center">
            <DollarSign className="h-5 w-5 mr-2 text-[#77A146]" />
            {t('sections.pricing')}
          </h2>
          <div className="bg-gray-50 rounded-lg p-4">
            {requestDetails.priceBreakdown && requestDetails.priceBreakdown.length > 0 ? (
              <div className="space-y-4">
                {/* Base Price Section */}
                {requestDetails.priceBreakdown.filter(item => item.label === requestDetails.adventureName || item.label.toLowerCase().includes('base')).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Base Price</h4>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                      {requestDetails.priceBreakdown
                        .filter(item => item.label === requestDetails.adventureName || item.label.toLowerCase().includes('base'))
                        .map((item, index) => (
                          <div key={index} className="pb-2 last:pb-0">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-semibold text-gray-900 flex-1">{item.label}</span>
                              <span className="font-bold text-primary ml-3 flex-shrink-0 text-lg">
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: requestDetails.estimatedPrice.currency
                                }).format(item.amount)}
                              </span>
                            </div>
                            {item.description && (
                              <div className="text-xs text-blue-600 font-mono mt-1">
                                {item.description}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* Add-ons Section */}
                {requestDetails.priceBreakdown.filter(item => item.label !== requestDetails.adventureName && !item.label.toLowerCase().includes('base')).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Add-ons & Extras</h4>
                    <div className="space-y-2">
                      {requestDetails.priceBreakdown
                        .filter(item => item.label !== requestDetails.adventureName && !item.label.toLowerCase().includes('base'))
                        .map((item, index) => (
                          <div key={index} className="border-b border-gray-200 last:border-b-0 pb-2 last:pb-0">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium text-gray-900 flex-1">{item.label}</span>
                              <span className="font-semibold text-gray-900 ml-3 flex-shrink-0">
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: requestDetails.estimatedPrice.currency
                                }).format(item.amount)}
                              </span>
                            </div>
                            {item.description && (
                              <div className="text-xs text-blue-600 font-mono mt-0.5">
                                {item.description}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* Total */}
                <div className="flex justify-between items-center pt-3 mt-3 border-t-2 border-gray-300">
                  <span className="text-lg font-semibold text-gray-900">{t('fields.estimatedPrice')}:</span>
                  <span className="text-2xl font-bold text-[#77A146]">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: requestDetails.estimatedPrice.currency
                    }).format(requestDetails.estimatedPrice.totalEstimate)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-gray-900">
                  {t('fields.estimatedPrice')}:
                </span>
                <span className="text-2xl font-bold text-[#77A146]">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: requestDetails.estimatedPrice.currency
                  }).format(requestDetails.estimatedPrice.totalEstimate)}
                </span>
              </div>
            )}
            <p className="text-sm text-gray-600 mt-2">
              {t('fields.priceNote')}
            </p>
          </div>
        </div>

        {/* Additional Preferences - Only show options NOT in price breakdown */}
        {Object.keys(requestDetails.selectedOptions).length > 0 && (() => {
          // Get breakdown labels to filter out duplicates
          const breakdownLabels = requestDetails.priceBreakdown?.map(item => item.label.toLowerCase()) || [];
          // Filter out options that are already shown in breakdown and people/days fields
          const filteredOptions = Object.entries(requestDetails.selectedOptions).filter(([key, value]) => {
            const keyLower = key.toLowerCase();
            // Skip if already in breakdown
            if (breakdownLabels.some(label => label.includes(keyLower) || keyLower.includes(label))) return false;
            // Skip people/days fields (shown in Trip Details)
            if (keyLower.includes('people') || keyLower.includes('person') || keyLower.includes('day') || keyLower.includes('days')) return false;
            // Skip boolean true values for options that might be in breakdown
            if (value === true && breakdownLabels.some(label => keyLower.includes(label.replace(/\s+/g, '')))) return false;
            return true;
          });
          
          return filteredOptions.length > 0 ? (
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900">
                Additional Preferences
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {filteredOptions.map(([key, value]) => (
                  <div key={key} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-sm font-medium text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                    <span className="text-sm text-gray-900 ml-3 text-right flex-shrink-0 max-w-[60%]">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {/* Important Note */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-amber-600 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-amber-900 mb-2">
                {t('importantNote.title')}
              </h3>
              <p className="text-amber-800 mb-2">
                {t('importantNote.description')}
              </p>
              <p className="text-sm text-amber-700">
                {t('importantNote.contact')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
